import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { initialEdges, initialNodes } from '@/lib/blueprintDefaults';
import { Blueprint } from '@/types';
import { toast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Copy,
  Cpu,
  Globe,
  Lock,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import EditorTabBar, { EditorTabItem } from './EditorTabBar';
import BlueprintCanvas from '@/pages/BlueprintCanvas';

interface AIBlueprintsProps {
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
  initialBlueprintId?: string;
}

type BlueprintListItem = Blueprint & {
  authorName: string | null;
};

type BlueprintsTab = {
  id: string;
  kind: 'home' | 'blueprint';
  title: string;
  path?: string;
  blueprintId?: string;
};

type BlueprintsTabState = {
  tabs: BlueprintsTab[];
  activeId: string;
};

type PersistedBlueprintsTabState = {
  tabs?: BlueprintsTab[];
  activeId?: string;
};

const HOME_TAB: BlueprintsTab = {
  id: 'home',
  kind: 'home',
  title: 'Home',
};

const ensureHomeTab = (tabs: BlueprintsTab[]) => [HOME_TAB, ...tabs.filter((tab) => tab.id !== HOME_TAB.id)];

const createBlueprintTab = (blueprintId: string, title?: string | null): BlueprintsTab => ({
  id: blueprintId,
  kind: 'blueprint',
  title: title?.trim() || 'Untitled Blueprint',
  path: `ai-blueprints:${blueprintId}`,
  blueprintId,
});

const BlueprintCard = ({
  blueprint,
  isOwner,
  onOpen,
  onClone,
  onDelete,
}: {
  blueprint: BlueprintListItem;
  isOwner: boolean;
  onOpen: (blueprintId: string, title?: string | null) => void;
  onClone: (blueprint: BlueprintListItem) => void;
  onDelete: (blueprint: BlueprintListItem) => void;
}) => {
  return (
    <Card className="group flex h-full flex-col overflow-hidden border-border/60 bg-card/80 transition-colors hover:border-primary/40">
      <button
        type="button"
        onClick={() => onOpen(blueprint.id, blueprint.name)}
        className="flex flex-1 flex-col text-left"
      >
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="flex min-w-0 items-center gap-2 text-base">
              <Cpu className="h-5 w-5 shrink-0 text-primary" />
              <span className="truncate">{blueprint.name}</span>
            </CardTitle>
            <div className="flex items-center gap-2 text-muted-foreground">
              {blueprint.is_private ? (
                <Lock className="h-4 w-4" aria-label="Private blueprint" />
              ) : (
                <Globe className="h-4 w-4" aria-label="Public blueprint" />
              )}
            </div>
          </div>
          <CardDescription className="line-clamp-3 min-h-[3.75rem] pt-1">
            {blueprint.description || 'No description provided.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1" />
      </button>

      <CardFooter className="mt-auto flex items-end justify-between gap-3 border-t border-border/60 pt-4">
        <div className="min-w-0 space-y-1 text-xs text-muted-foreground">
          <p className="truncate">By {blueprint.authorName || 'Unknown'}</p>
          <p>Updated {new Date(blueprint.updated_at).toLocaleDateString()}</p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {isOwner ? (
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-destructive"
              onClick={(event) => {
                event.stopPropagation();
                onDelete(blueprint);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={(event) => {
                event.stopPropagation();
                onClone(blueprint);
              }}
            >
              <Copy className="h-4 w-4" />
              Clone
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};

const AIBlueprints = ({ onOpenWindow, initialBlueprintId }: AIBlueprintsProps) => {
  const { user } = useAuth();
  const sb = supabase as any;
  const [myBlueprints, setMyBlueprints] = useState<BlueprintListItem[]>([]);
  const [publicBlueprints, setPublicBlueprints] = useState<BlueprintListItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [blueprintToDelete, setBlueprintToDelete] = useState<BlueprintListItem | null>(null);
  const [{ tabs, activeId }, setTabState] = useState<BlueprintsTabState>({
    tabs: [HOME_TAB],
    activeId: HOME_TAB.id,
  });
  const restoredUserIdRef = useRef<string | null>(null);
  const initialBlueprintOpenedRef = useRef<string | null>(null);

  const fetchBlueprints = useCallback(async () => {
    if (!user) {
      setMyBlueprints([]);
      setPublicBlueprints([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const { data, error } = await sb
      .from('blueprints')
      .select('id, name, description, updated_at, is_private, user_id, created_at, organization_id, data, profiles(full_name)')
      .or(`is_private.eq.false,user_id.eq.${user.id}`)
      .order('updated_at', { ascending: false });

    if (error) {
      toast({ title: 'Error fetching blueprints', description: error.message, variant: 'destructive' });
      setLoading(false);
      return;
    }

    const formatted = (data || []).map((item: any) => ({
      ...item,
      authorName:
        item?.profiles && typeof item.profiles === 'object' && 'full_name' in item.profiles
          ? item.profiles.full_name || 'Unknown'
          : 'Unknown',
    })) as BlueprintListItem[];

    setMyBlueprints(formatted.filter((blueprint) => blueprint.user_id === user.id));
    setPublicBlueprints(
      formatted.filter((blueprint) => blueprint.user_id !== user.id && blueprint.is_private === false)
    );
    setLoading(false);
  }, [sb, user]);

  useEffect(() => {
    restoredUserIdRef.current = null;
  }, [user?.id]);

  useEffect(() => {
    void fetchBlueprints();
  }, [fetchBlueprints]);

  const allBlueprints = useMemo(() => [...myBlueprints, ...publicBlueprints], [myBlueprints, publicBlueprints]);
  const blueprintMap = useMemo(
    () => new Map(allBlueprints.map((blueprint) => [blueprint.id, blueprint])),
    [allBlueprints]
  );
  const storageKey = user ? `ai-blueprints-tabs:${user.id}` : null;

  useEffect(() => {
    if (!user) {
      restoredUserIdRef.current = null;
      setTabState({ tabs: [HOME_TAB], activeId: HOME_TAB.id });
      return;
    }

    if (loading || restoredUserIdRef.current === user.id) {
      return;
    }

    let nextState: BlueprintsTabState = { tabs: [HOME_TAB], activeId: HOME_TAB.id };

    if (storageKey) {
      try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
          const parsed = JSON.parse(raw) as PersistedBlueprintsTabState;
          const restoredTabs = (parsed.tabs || [])
            .filter((tab): tab is BlueprintsTab => tab.kind === 'blueprint' && Boolean(tab.blueprintId))
            .map((tab) => {
              const blueprint = blueprintMap.get(tab.blueprintId!);
              return blueprint ? createBlueprintTab(blueprint.id, blueprint.name) : null;
            })
            .filter((tab): tab is BlueprintsTab => Boolean(tab));

          const normalizedTabs = ensureHomeTab(restoredTabs);
          nextState = {
            tabs: normalizedTabs,
            activeId:
              parsed.activeId && normalizedTabs.some((tab) => tab.id === parsed.activeId)
                ? parsed.activeId
                : HOME_TAB.id,
          };
        }
      } catch (error) {
        console.error('Error restoring AI blueprint tabs:', error);
      }
    }

    restoredUserIdRef.current = user.id;
    setTabState(nextState);
  }, [blueprintMap, loading, storageKey, user]);

  useEffect(() => {
    if (!user || restoredUserIdRef.current !== user.id || loading) {
      return;
    }

    setTabState((previousState) => {
      let changed = false;

      const syncedTabs = ensureHomeTab(
        previousState.tabs.flatMap((tab) => {
          if (tab.kind === 'home') {
            return [HOME_TAB];
          }

          if (!tab.blueprintId) {
            changed = true;
            return [];
          }

          const blueprint = blueprintMap.get(tab.blueprintId);
          if (!blueprint) {
            changed = true;
            return [];
          }

          const nextTab = createBlueprintTab(blueprint.id, blueprint.name);
          if (tab.title !== nextTab.title || tab.path !== nextTab.path) {
            changed = true;
          }

          return [nextTab];
        })
      );

      const nextActiveId = syncedTabs.some((tab) => tab.id === previousState.activeId)
        ? previousState.activeId
        : HOME_TAB.id;

      if (!changed && syncedTabs.length === previousState.tabs.length && nextActiveId === previousState.activeId) {
        return previousState;
      }

      return {
        tabs: syncedTabs,
        activeId: nextActiveId,
      };
    });
  }, [blueprintMap, loading, user]);

  useEffect(() => {
    if (!storageKey || !user || restoredUserIdRef.current !== user.id) {
      return;
    }

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        tabs: tabs.filter((tab) => tab.kind === 'blueprint'),
        activeId: tabs.some((tab) => tab.id === activeId) ? activeId : HOME_TAB.id,
      } satisfies PersistedBlueprintsTabState)
    );
  }, [activeId, storageKey, tabs, user]);

  const openBlueprintTab = useCallback((blueprintId: string, title?: string | null) => {
    setTabState((previousState) => {
      const nextTitle = title?.trim() || blueprintMap.get(blueprintId)?.name || 'Untitled Blueprint';
      const existingTab = previousState.tabs.find((tab) => tab.id === blueprintId);

      if (existingTab) {
        return {
          tabs: previousState.tabs.map((tab) =>
            tab.id === blueprintId ? createBlueprintTab(blueprintId, nextTitle) : tab
          ),
          activeId: blueprintId,
        };
      }

      return {
        tabs: [...previousState.tabs, createBlueprintTab(blueprintId, nextTitle)],
        activeId: blueprintId,
      };
    });
  }, [blueprintMap]);

  useEffect(() => {
    if (!initialBlueprintId || !user || loading) {
      return;
    }

    const initialBlueprintKey = `${user.id}:${initialBlueprintId}`;
    if (initialBlueprintOpenedRef.current === initialBlueprintKey) {
      return;
    }

    initialBlueprintOpenedRef.current = initialBlueprintKey;
    openBlueprintTab(initialBlueprintId, blueprintMap.get(initialBlueprintId)?.name);
  }, [blueprintMap, initialBlueprintId, loading, openBlueprintTab, user]);

  const closeTab = useCallback((id: string) => {
    if (id === HOME_TAB.id) {
      return;
    }

    setTabState((previousState) => {
      const tabIndex = previousState.tabs.findIndex((tab) => tab.id === id);
      if (tabIndex === -1) {
        return previousState;
      }

      const nextTabs = ensureHomeTab(previousState.tabs.filter((tab) => tab.id !== id));
      const nextActiveId =
        previousState.activeId === id
          ? previousState.tabs
              .slice(0, tabIndex)
              .reverse()
              .find((tab) => tab.id !== id)?.id || HOME_TAB.id
          : nextTabs.some((tab) => tab.id === previousState.activeId)
            ? previousState.activeId
            : HOME_TAB.id;

      return {
        tabs: nextTabs,
        activeId: nextActiveId,
      };
    });
  }, []);

  const closeOthers = useCallback((id: string) => {
    if (id === HOME_TAB.id) {
      return;
    }

    setTabState((previousState) => {
      const selectedTab = previousState.tabs.find((tab) => tab.id === id);
      if (!selectedTab) {
        return previousState;
      }

      return {
        tabs: ensureHomeTab([selectedTab]),
        activeId: selectedTab.id,
      };
    });
  }, []);

  const closeRight = useCallback((id: string) => {
    if (id === HOME_TAB.id) {
      return;
    }

    setTabState((previousState) => {
      const selectedIndex = previousState.tabs.findIndex((tab) => tab.id === id);
      if (selectedIndex === -1) {
        return previousState;
      }

      const nextTabs = ensureHomeTab(
        previousState.tabs.filter((tab, index) => tab.id === HOME_TAB.id || index <= selectedIndex)
      );

      return {
        tabs: nextTabs,
        activeId: nextTabs.some((tab) => tab.id === previousState.activeId) ? previousState.activeId : id,
      };
    });
  }, []);

  const reorderTabs = useCallback((orderedIds: string[]) => {
    setTabState((previousState) => {
      const blueprintTabs = previousState.tabs.filter((tab) => tab.kind === 'blueprint');
      const blueprintTabMap = new Map(blueprintTabs.map((tab) => [tab.id, tab]));
      const orderedBlueprintTabs = orderedIds
        .filter((id) => id !== HOME_TAB.id)
        .map((id) => blueprintTabMap.get(id))
        .filter((tab): tab is BlueprintsTab => Boolean(tab));
      const remainingTabs = blueprintTabs.filter(
        (tab) => !orderedBlueprintTabs.some((orderedTab) => orderedTab.id === tab.id)
      );

      return {
        ...previousState,
        tabs: [HOME_TAB, ...orderedBlueprintTabs, ...remainingTabs],
      };
    });
  }, []);

  const isUniqueViolation = (error: any) => {
    return error?.code === '23505' || String(error?.message || '').includes('unique_user_blueprint_name');
  };

  const createBlueprintWithUniqueName = useCallback(async (
    baseName: string,
    payload: { description: string; data: Blueprint['data']; is_private?: boolean }
  ) => {
    if (!user) {
      return { id: null as string | null, name: null as string | null };
    }

    for (let index = 0; index < 20; index += 1) {
      const name = index === 0 ? baseName : `${baseName} (${index + 1})`;
      const { data, error } = await sb
        .from('blueprints')
        .insert({
          user_id: user.id,
          name,
          description: payload.description,
          data: payload.data,
          ...(typeof payload.is_private === 'boolean' ? { is_private: payload.is_private } : {}),
        })
        .select('id, name')
        .single();

      if (!error) {
        return { id: data?.id ?? null, name: data?.name ?? name };
      }

      if (!isUniqueViolation(error)) {
        throw error;
      }
    }

    throw new Error('Could not create a unique blueprint name. Please rename an existing blueprint and try again.');
  }, [sb, user]);

  const handleCreateBlueprint = useCallback(async () => {
    if (!user) {
      toast({ title: 'Authentication Required', description: 'Please sign in to create blueprints.', variant: 'destructive' });
      return;
    }

    try {
      const { id, name } = await createBlueprintWithUniqueName('Untitled Blueprint', {
        description: 'A new blueprint ready for design.',
        data: { nodes: initialNodes, edges: initialEdges },
        is_private: true,
      });

      if (id) {
        await fetchBlueprints();
        openBlueprintTab(id, name);
      }
    } catch (error: any) {
      toast({ title: 'Error creating blueprint', description: error.message, variant: 'destructive' });
    }
  }, [createBlueprintWithUniqueName, fetchBlueprints, openBlueprintTab, user]);

  const handleCloneBlueprint = useCallback(async (blueprintToClone: BlueprintListItem) => {
    if (!user) {
      toast({ title: 'Authentication Required', description: 'Please sign in to clone blueprints.', variant: 'destructive' });
      return;
    }

    try {
      const { id, name } = await createBlueprintWithUniqueName(`Clone of ${blueprintToClone.name}`, {
        description: blueprintToClone.description || 'No description provided.',
        data: blueprintToClone.data,
        is_private: true,
      });

      if (id) {
        toast({ title: 'Blueprint Cloned!', description: 'Opening your cloned blueprint...' });
        await fetchBlueprints();
        openBlueprintTab(id, name);
      }
    } catch (error: any) {
      toast({ title: 'Error cloning blueprint', description: error.message, variant: 'destructive' });
    }
  }, [createBlueprintWithUniqueName, fetchBlueprints, openBlueprintTab, user]);

  const confirmDeleteBlueprint = useCallback(async () => {
    if (!blueprintToDelete || !user) {
      return;
    }

    const { error } = await sb
      .from('blueprints')
      .delete()
      .eq('id', blueprintToDelete.id)
      .eq('user_id', user.id);

    if (error) {
      toast({ title: 'Error deleting blueprint', description: error.message, variant: 'destructive' });
      return;
    }

    toast({ title: 'Blueprint Deleted', description: `${blueprintToDelete.name} has been removed.` });
    setBlueprintToDelete(null);
    closeTab(blueprintToDelete.id);
    await fetchBlueprints();
  }, [blueprintToDelete, closeTab, fetchBlueprints, sb, user]);

  const filteredMyBlueprints = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return myBlueprints;
    }

    return myBlueprints.filter((blueprint) => {
      return (
        blueprint.name.toLowerCase().includes(query) ||
        (blueprint.description || '').toLowerCase().includes(query)
      );
    });
  }, [myBlueprints, searchTerm]);

  const filteredPublicBlueprints = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return publicBlueprints;
    }

    return publicBlueprints.filter((blueprint) => {
      return (
        blueprint.name.toLowerCase().includes(query) ||
        (blueprint.description || '').toLowerCase().includes(query) ||
        (blueprint.authorName || '').toLowerCase().includes(query)
      );
    });
  }, [publicBlueprints, searchTerm]);

  const editorTabs = useMemo<EditorTabItem[]>(() => {
    return tabs.map((tab) => ({
      id: tab.id,
      name: tab.title,
      path: tab.path || '',
      isPinned: tab.kind === 'home',
    }));
  }, [tabs]);

  const activeTab = useMemo(() => tabs.find((tab) => tab.id === activeId) || HOME_TAB, [activeId, tabs]);

  const popOutTab = useCallback((id: string) => {
    const tab = tabs.find((candidate) => candidate.id === id && candidate.kind === 'blueprint');
    if (!tab?.blueprintId) {
      return;
    }

    onOpenWindow?.('ai-blueprints', `AI Blueprints - ${tab.title}`, tab.blueprintId);
  }, [onOpenWindow, tabs]);

  if (!user) {
    return (
      <div className="flex h-full items-center justify-center bg-background p-6">
        <p className="text-sm text-muted-foreground">Sign in to view AI Blueprints in MapleOS.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="pt-[5px]">
        <EditorTabBar
          tabs={editorTabs}
          activeId={activeId}
          onSelect={(id) => setTabState((previousState) => ({ ...previousState, activeId: id }))}
          onClose={closeTab}
          onReorder={reorderTabs}
          onNewUntitled={() => {
            void handleCreateBlueprint();
          }}
          onCloseOthers={closeOthers}
          onCloseRight={closeRight}
          onSave={() => undefined}
          onSaveAll={() => undefined}
          onRevealInExplorer={() => undefined}
          onCopyPath={() => undefined}
          onPopOut={popOutTab}
          showSaveItems={false}
          showRevealItems={false}
        />
      </div>

      <div className="min-h-0 flex-1">
        {activeTab.kind === 'home' ? (
          <div className="h-full overflow-y-auto">
            <div className="mx-auto flex min-h-full max-w-7xl flex-col gap-6 p-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h1 className="flex items-center gap-3 text-3xl font-bold text-foreground">
                    <Cpu className="h-8 w-8 text-primary" />
                    AI Blueprints
                  </h1>
                  <p className="mt-1 text-muted-foreground">
                    Open, clone, and manage your AI blueprint flows inside MapleOS.
                  </p>
                </div>

                <Button onClick={() => void handleCreateBlueprint()} className="gap-2">
                  <Plus className="h-4 w-4" />
                  New Blueprint
                </Button>
              </div>

              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="relative w-full max-w-xl">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search blueprints..."
                    className="pl-9"
                  />
                </div>

                <div className="text-sm text-muted-foreground">
                  {allBlueprints.length} {allBlueprints.length === 1 ? 'blueprint' : 'blueprints'}
                </div>
              </div>

              {loading ? (
                <div className="space-y-10">
                  <div className="space-y-4">
                    <Skeleton className="h-7 w-40" />
                    <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                      {Array.from({ length: 6 }).map((_, index) => (
                        <Card key={index}>
                          <CardHeader>
                            <Skeleton className="h-6 w-2/3" />
                            <Skeleton className="h-4 w-full" />
                            <Skeleton className="h-4 w-4/5" />
                          </CardHeader>
                          <CardFooter className="flex justify-between">
                            <Skeleton className="h-8 w-32" />
                            <Skeleton className="h-8 w-8" />
                          </CardFooter>
                        </Card>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-10">
                  <section className="space-y-4">
                    <div>
                      <h2 className="text-xl font-semibold">My Blueprints</h2>
                      <p className="text-sm text-muted-foreground">Blueprints you own and can edit.</p>
                    </div>

                    {filteredMyBlueprints.length > 0 ? (
                      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                        {filteredMyBlueprints.map((blueprint) => (
                          <BlueprintCard
                            key={blueprint.id}
                            blueprint={blueprint}
                            isOwner
                            onOpen={openBlueprintTab}
                            onClone={handleCloneBlueprint}
                            onDelete={setBlueprintToDelete}
                          />
                        ))}
                      </div>
                    ) : myBlueprints.length > 0 ? (
                      <div className="rounded-lg border border-dashed border-muted-foreground/30 bg-muted/10 px-6 py-12 text-center">
                        <h3 className="text-lg font-semibold">No matching blueprints</h3>
                        <p className="mt-2 text-sm text-muted-foreground">Try a different search term.</p>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-muted-foreground/30 bg-muted/10 px-6 py-16 text-center">
                        <Cpu className="mx-auto h-12 w-12 text-muted-foreground" />
                        <h3 className="mt-4 text-xl font-semibold">No blueprints yet</h3>
                        <p className="mt-2 text-sm text-muted-foreground">Create your first blueprint to start building AI flows.</p>
                        <Button onClick={() => void handleCreateBlueprint()} className="mt-6 gap-2">
                          <Plus className="h-4 w-4" />
                          New Blueprint
                        </Button>
                      </div>
                    )}
                  </section>

                  {publicBlueprints.length > 0 && (
                    <section className="space-y-4">
                      <div>
                        <h2 className="text-xl font-semibold">Public Blueprints</h2>
                        <p className="text-sm text-muted-foreground">Public blueprints shared by other users.</p>
                      </div>

                      {filteredPublicBlueprints.length > 0 ? (
                        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                          {filteredPublicBlueprints.map((blueprint) => (
                            <BlueprintCard
                              key={blueprint.id}
                              blueprint={blueprint}
                              isOwner={false}
                              onOpen={openBlueprintTab}
                              onClone={handleCloneBlueprint}
                              onDelete={setBlueprintToDelete}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="rounded-lg border border-dashed border-muted-foreground/30 bg-muted/10 px-6 py-12 text-center">
                          <h3 className="text-lg font-semibold">No matching public blueprints</h3>
                          <p className="mt-2 text-sm text-muted-foreground">Try a different search term.</p>
                        </div>
                      )}
                    </section>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <BlueprintCanvas
            key={activeTab.id}
            blueprintIdOverride={activeTab.blueprintId}
            embeddedInMapleOS
            onRequestClose={() => closeTab(activeTab.id)}
            onOpenBlueprint={(blueprintId) => openBlueprintTab(blueprintId, blueprintMap.get(blueprintId)?.name)}
          />
        )}
      </div>

      <AlertDialog open={Boolean(blueprintToDelete)} onOpenChange={(open) => !open && setBlueprintToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete blueprint?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. "{blueprintToDelete?.name}" will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setBlueprintToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmDeleteBlueprint()}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AIBlueprints;
